import React, {useId, useMemo, useState} from 'react';

import clsx from 'clsx';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';

import {FieldDescription, Types as FieldDescriptionTypes} from '@pb/kit-component-field-description';
import {Icon, Icons, Sizes} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';


import {
    inputAddorment,
    inputAddormentItem,
    inputAddormentItemClickable,
} from './input.module.styl';

type Props = {
    id: string,
    value: string,
    isNotEmpty: boolean,
    onBlur: (value: string) => void,
    onClear: () => void,

    label?: string,
    description?: string,

    maxLength?: number,
    width?: number,
    placeholder?: string,

    readOnly?: boolean,
    disabled?: boolean,
};

export const PasswordInput = (props: Props) => {
    const {
        id,
        value,
        isNotEmpty,
        onBlur,
        onClear,

        label,
        description,

        maxLength,
        width,
        placeholder,

        readOnly,
        disabled,
    } = props;

    const labelId = useId();
    const descriptionId = useId();

    const [internalValue, setInternalValue] = useState(value);

    useEffectWithDeps((prev, current) => {
        if (prev !== current) {
            setInternalValue(current);
        }
    }, value);

    const endAdornment = useMemo(() => {
        const clickableItemClasses = clsx(inputAddormentItem, [
            !readOnly && !disabled && inputAddormentItemClickable,
        ]);

        return (
            <div className={inputAddorment}>
                {isNotEmpty ? (
                    <Icon
                        className={clickableItemClasses}
                        type={Icons.CLOSE}
                        size={Sizes.Size_19}
                        onClick={!readOnly && !disabled ? () => {
                            setInternalValue('');
                            onClear();
                        } : undefined}
                    />
                ) : null}
                {readOnly ? <Icon className={inputAddormentItem} type={Icons.LOCK} size="Size_14"/> : null}
            </div>
        );
    }, [readOnly, isNotEmpty, disabled, onClear]);

    return (
        <div style={{width}}>
            {!!label && <Label id={labelId}>{label}</Label>}
            <Input
                id={id}
                value={internalValue}
                type="password"

                onInput={event => setInternalValue(event.currentTarget.value)}
                onKeyUp={event => {
                    if (event.key === 'Enter') {
                        event.currentTarget.blur();
                    }
                }}
                onBlur={() => {
                    if (value !== internalValue) {
                        onBlur(internalValue.trim());
                    }
                }}

                autoComplete="off"
                autoCorrect="off"
                aria-labelledby={label ? labelId : undefined}
                aria-describedby={description ? descriptionId : undefined}

                maxLength={maxLength}
                placeholder={isNotEmpty ? '************' : placeholder}
                endAdornment={endAdornment}
                disabled={disabled}
                readOnly={readOnly}
            />
            {!!description && (
                <FieldDescription aria-live="polite" id={descriptionId} color={FieldDescriptionTypes.DEFAULT}>
                    {description}
                </FieldDescription>
            )}
        </div>
    );
};
